// LayoutRollupPickerModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getResolvedFields } from 'utilities/NodeType';
import { getNodeTypePluralLabelFromDefinition } from 'utilities/NodeTypeLabels';

// Services
import { normalizeSearchText, tokenizeSearchQuery } from 'services/search/SearchIndexService';

// CSS
import './LayoutRollupPickerModal.css';

// Components
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';
import { normalizeRollupDefinitions, summarizeRollupDefinition } from 'rollups/rollupDefinitions';

/**
 * LayoutRollupPickerModal
 *
 * @returns {ReactNode} The rendered component.
 */
const LayoutRollupPickerModal = React.memo(({
	isOpen,
	onClose,
	onSelect,
}) => {

	// ----- CONTEXT ----- //

	const { getAll } = useNodeTypes();
	// ----- DEVICE DETECTION ----- //

	const isMobile = useIsMobile();

	// ----- STATE ----- //

	const [query, setQuery] = useState('');
	const [selectingKey, setSelectingKey] = useState('');

	// ----- EFFECTS ----- //

	useEffect(() => {
		if (!isOpen) return;

		setQuery('');
		setSelectingKey('');
	}, [isOpen]);

	// ----- MEMOS ----- //

	const queryTokens = useMemo(() => tokenizeSearchQuery(query), [query]);
	const rows = useMemo(() => {
		const typeDefs = Array.isArray(getAll?.()) ? getAll() : [];
		return typeDefs
			.flatMap((typeDef) => {
				const typeKey = String(typeDef?.key || '').trim();
				if (!typeKey) return [];

				const normalizedRollups = normalizeRollupDefinitions(typeDef?.rollups, { typeKey });
				if (!normalizedRollups.length) return [];

				const fieldMap = new Map(
					getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true })
						.map((field) => {
							const key = String(field?.name || field?.key || '').trim();
							return key ? [key, field] : null;
						})
						.filter(Boolean)
				);
				const typeLabel = getNodeTypePluralLabelFromDefinition(typeDef, typeKey, { fallback: 'Items' });

				return normalizedRollups.map((rollup) => {
					const title = String(rollup?.title || '').trim() || 'Untitled rollup';
					const note = summarizeRollupDefinition(rollup, fieldMap);
					const haystack = normalizeSearchText([typeLabel, title, note].join(' '));

					return {
						haystack,
						key: `${typeKey}:${rollup.key}`,
						note,
						rollup,
						title,
						typeKey,
						typeLabel,
					};
				});
			})
			.filter((row) => {
				if (!queryTokens.length) return true;
				return queryTokens.every((token) => row.haystack.includes(token));
			})
			.sort((left, right) => {
				if (left.typeLabel !== right.typeLabel) {
					return left.typeLabel.localeCompare(right.typeLabel);
				}

				return left.title.localeCompare(right.title);
			});
	}, [getAll, queryTokens]);
	const noResults = Boolean(queryTokens.length) && !rows.length;

	/**
	 * handleSelectRow
	 *
	 * Handles select row.
	 *
	 * @param {object} row - row.
	 * @returns {Promise<void>} Resolves when the handler completes.
	 */
	async function handleSelectRow(row) {
		if (!row?.rollup?.key || !row?.typeKey) return;

		setSelectingKey(row.key);

		try {
			await onSelect?.({
				rollupKey: row.rollup.key,
				typeKey: row.typeKey,
			});
			onClose?.();
		} finally {
			setSelectingKey('');
		}
	}
	// ----- RENDER ----- //

	return (
		<Modal
			height="80%"
			isOpen={isOpen}
			title="Add Rollup"
			width={isMobile ? '100%' : 640}
			onClose={onClose}
		>
			<div className="page-rollup-picker-modal-container">

				{/* SEARCH */}
				<div className="page-rollup-picker-modal-search">
					<TextInput
						autoFocus
						placeholder="Search rollups"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>
				</div>

				{/* STATUS */}
				{noResults ? (
					<div
						className="page-rollup-picker-modal-status"
					>
						No matching rollups found.
					</div>
				) : null}

				{/* RESULTS */}
				<div className="page-rollup-picker-modal-results">
					{rows.map((row) => {
						const isSelecting = selectingKey === row.key;

						return (
							<div
								className="page-rollup-picker-modal-result-button"
								style={{
									opacity: isSelecting ? 0.6 : 1,
								}}
								key={row.key}
								onClick={() => handleSelectRow(row)}
							>

								{/* RESULT TITLE */}
								<div
									className="page-rollup-picker-modal-result-title"
								>
									{row.typeLabel} &gt; {row.title}
								</div>

								{/* RESULT NOTE */}
								<div
									className="page-rollup-picker-modal-result-note"
								>
									{row.note}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</Modal>
	);
});

// Dev Tools
LayoutRollupPickerModal.displayName = 'LayoutRollupPickerModal';

export default LayoutRollupPickerModal;
